import {
  MEETING_DEBUG_CAPS,
  type MeetingDebugEvent,
} from '@electron/shared/meeting-debug.types';
import { groupBy } from 'lodash';
import { makeAutoObservable, observable, runInAction } from 'mobx';

import { electronApis } from '@/features/electron-api';
import { meetingStore } from '@/features/meetings/meetings.state';
import { userStore } from '@/stores/user.store';

import type { MeetingHeartbeatHealth } from '@electron/shared/meetings.types';

export type MeetingDebugEntry = Omit<
  MeetingDebugEvent,
  'at' | 'source' | 'meetingId'
> & {
  /** `undefined` files the entry under the live session, `null` under the global log */
  meetingId?: string | null;
};

const liveMeetingId = () => {
  const { recording } = meetingStore;
  return recording.status === 'idle' ? undefined : recording.meetingId;
};

const appendCapped = (
  events: MeetingDebugEvent[],
  event: MeetingDebugEvent,
  cap: number
) => [...events, event].slice(-cap);

class MeetingDebugStore {
  globalEvents: MeetingDebugEvent[] = [];
  eventsByMeetingId = observable.map<string, MeetingDebugEvent[]>();
  heartbeatHealth: MeetingHeartbeatHealth | undefined = undefined;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  record(entry: MeetingDebugEntry) {
    if (!userStore.isInternalUser) return;

    const event: MeetingDebugEvent = {
      at: Date.now(),
      source: 'renderer',
      ...entry,
      meetingId: (entry.meetingId === undefined
        ? liveMeetingId()
        : (entry.meetingId ?? undefined)
      )?.toLowerCase(),
    };

    if (event.meetingId) {
      this.eventsByMeetingId.set(
        event.meetingId,
        appendCapped(
          this.eventsByMeetingId.get(event.meetingId) ?? [],
          event,
          MEETING_DEBUG_CAPS.entriesPerMeeting
        )
      );
    } else {
      this.globalEvents = appendCapped(
        this.globalEvents,
        event,
        MEETING_DEBUG_CAPS.globalEntries
      );
    }
    electronApis.meetingDebug.append(event);
  }

  setHeartbeatHealth(health: MeetingHeartbeatHealth | undefined) {
    this.heartbeatHealth = health;
  }

  async load(meetingId: string | undefined) {
    const { meeting, global } = await electronApis.meetingDebug.read(meetingId);
    runInAction(() => {
      this.globalEvents = global;
      if (meetingId) {
        this.eventsByMeetingId.set(meetingId.toLowerCase(), meeting);
        return;
      }
      this.eventsByMeetingId.replace(
        groupBy(meeting, (event) => event.meetingId ?? '')
      );
    });
  }

  eventsForMeeting(meetingId: string): MeetingDebugEvent[] {
    return this.eventsByMeetingId.get(meetingId.toLowerCase()) ?? [];
  }

  latestDetection(meetingId: string): MeetingDebugEvent | undefined {
    const events = [...this.globalEvents, ...this.eventsForMeeting(meetingId)];
    return events
      .filter((event) => event.kind === 'apps_updated')
      .sort((a, b) => a.at - b.at)
      .at(-1);
  }
}

export const meetingDebugStore = new MeetingDebugStore();
